import { useMemo } from "react";

import AccountBoxRoundedIcon from "@mui/icons-material/AccountBoxRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import HowToRegRoundedIcon from "@mui/icons-material/HowToRegRounded";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { formatShortWeekdayDate, formatTime, formatWeekdayDateTime } from "@shared";
import { isSameDay, subMilliseconds } from "date-fns";

import { AppointmentForTakeoverResponse } from "@/api";
import { useCurrentUserContext } from "@/common/contexts/current-user-context/CurrentUserContext";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { useAppointmentLabels } from "@/common/hooks/useAppointmentLabels";
import { useAppointmentsQuery } from "@/common/hooks/useAppointmentsQuery";
import { composeDetails, getFromUserCode } from "@/pages/takeover/Takeover.utils";
import { useTranslation } from "@/translations";

interface Props {
  appointment: AppointmentForTakeoverResponse;
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: (appointmentId: string) => void;
}

/**
 * Confirmation dialog for taking over an appointment. Shows the appointment to be taken over and
 * the current user's own appointments on the same day, so schedule conflicts can be assessed (AC3).
 */
export function TakeOverDialog({ appointment, isSubmitting, onClose, onConfirm }: Props) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const { currentUser } = useCurrentUserContext();
  const labels = useAppointmentLabels();

  const day = appointment.startTimeUtc;
  // Query the current user's appointments across the full span of the appointment being taken over,
  // so conflicts on every day it occupies are shown (a multi-day appointment isn't just its start day).
  const { appointments, isLoading } = useAppointmentsQuery({
    startDate: appointment.startTimeUtc,
    endDate: appointment.endTimeUtc,
    counselorId: currentUser.counselorId ?? null,
  });

  // Last day the appointment actually occupies. All-day appointments end at the next midnight, so
  // step back a moment to avoid counting that trailing day.
  const lastDay = subMilliseconds(appointment.endTimeUtc, 1);
  const isMultiDay = !isSameDay(day, lastDay);

  // Backend (getAppointmentsByDate) already orders by StartTimeUtc; just exclude the one being taken over.
  const ownAppointments = useMemo(
    () => appointments.filter((own) => own.appointmentId !== appointment.appointmentId),
    [appointments, appointment.appointmentId]
  );

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <AccountBoxRoundedIcon color="primary" />
          {t("takeover.takeOverDialog.title")}
        </Box>
        <IconButton aria-label={t("takeover.takeOverDialog.cancel")} onClick={onClose}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="overline" color="text.secondary">
              {t("takeover.takeOverDialog.appointmentSection")}
            </Typography>
            <Stack spacing={1} sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
              <Typography variant="h3">{formatWeekdayDateTime(day, locales[language])}</Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Chip
                  label={labels.purposeLabelOf(appointment.purposeKey)}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <Typography variant="body2">{composeDetails(appointment, labels)}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t("takeover.takeOverDialog.offeredBy", { userCode: getFromUserCode(appointment) })}
              </Typography>
            </Stack>
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary">
              {isMultiDay
                ? t("takeover.takeOverDialog.yourAppointmentsRangeSection", {
                    startDate: formatShortWeekdayDate(day, locales[language]),
                    endDate: formatShortWeekdayDate(lastDay, locales[language]),
                  })
                : t("takeover.takeOverDialog.yourAppointmentsSection", {
                    date: formatShortWeekdayDate(day, locales[language]),
                  })}
            </Typography>
            {!isLoading && ownAppointments.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t("takeover.takeOverDialog.noOwnAppointments")}
              </Typography>
            ) : (
              <Stack spacing={1}>
                {ownAppointments.map((own) => (
                  <Box
                    key={own.appointmentId}
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{ p: 1.5, border: 1, borderColor: "divider", borderRadius: 1 }}
                  >
                    <Typography variant="body2" fontWeight={700} sx={{ minWidth: 96 }}>
                      {own.isAllDay
                        ? t("takeover.allDay")
                        : `${formatTime(own.startTimeUtc, locales[language])} ${t("common.clock")}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130 }}>
                      {labels.purposeLabelOf(own.purposeKey)}
                    </Typography>
                    <Typography variant="body2">{composeDetails(own, labels)}</Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3, gap: 2, justifyContent: "space-between" }}>
        <Button variant="outlined" onClick={onClose}>
          {t("takeover.takeOverDialog.cancel")}
        </Button>
        <Button
          variant="contained"
          startIcon={<HowToRegRoundedIcon />}
          disabled={isSubmitting}
          onClick={() => onConfirm(appointment.appointmentId)}
        >
          {t("takeover.takeOverDialog.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
